import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftIcon, ArrowRightIcon, ArrowUturnLeftIcon, UserMinusIcon } from "@heroicons/react/24/outline";
import { Container, EmptyState, Loading, PageHeader, RowAction, Select, Table, Tag } from "../../ui";
import { pageParams, problemOf, scholarshipsApi } from "../../../api/scholarships";
import { SCHOLARSHIPS_KEY, closingOf, destinationOf, formatKigaliDate, stateOf } from "../model";
import { EVALUATION_STATES, evaluationStateOf, formatDateTime, fullName } from "../review/model";
import { ApplicantName } from "../review/EligibilityFlag";
import { ConsentCard, RemoveApplicantModal, restoreApplicant } from "./Consent";
import { CALL_KINDS, abroadQueuePath, useAbroadMarkingCalls } from "../../abroadLoans/EvaluatorPages";

export const EVALUATOR_KEY = [...SCHOLARSHIPS_KEY, "evaluator"];
const PAGE_SIZE = 20;

// Calls the evaluator can still mark; decided or cancelled ones are finished
const MARKABLE = ["PUBLISHED", "CLOSED"];
const leftOf = (s) => s.applicationsToEvaluate - s.mySubmitted;
// A scholarship whose applications are still open waits under "To mark"
const groupOf = (s) => (!MARKABLE.includes(s.state) ? "finished" : s.markingOpen === false || leftOf(s) > 0 ? "to-mark" : "marked");
// Each group is its own page, /evaluation/<value>
export const MARKING_GROUPS = [
  { value: "to-mark", title: "To mark", subtitle: "Calls with applications waiting for your marks, soonest closing first.", empty: "Nothing to mark" },
  { value: "marked", title: "All marked", subtitle: "Calls you finished marking, until HEC decides.", empty: "No fully marked calls" },
  { value: "finished", title: "Finished", subtitle: "Decided or cancelled calls, the latest first.", empty: "No finished calls" },
];
// Soonest closing first; the latest decided first
const byClosing = (a, b) => String(a.closedAt ?? a.closesAt ?? "").localeCompare(String(b.closedAt ?? b.closesAt ?? ""));

// An abroad call as a marking card reads it; it is listed while published, so never finished
const abroadCardOf = (c) => ({
  id: c.id,
  name: c.name,
  kind: c.kind,
  state: "PUBLISHED",
  closesAt: c.closesAt,
  applicationsToEvaluate: c.submitted - c.removedByMe,
  mySubmitted: c.evaluatedByMe,
  myDrafts: 0,
  myConflicts: c.removedByMe,
  path: abroadQueuePath(c.id),
});

// Scholarships are marked once applications end (abroad calls carry no flag)
export const MARKING_NOT_OPEN = "marking-not-open";
const notOpenYet = (s) => s.markingOpen === false;
const opensNote = (closesAt) => `Marking starts once applications close${closesAt ? ` on ${formatKigaliDate(closesAt)}` : ""}.`;

function MarkingCard({ scholarship: s, onOpen }) {
  const left = leftOf(s);
  const finished = groupOf(s) === "finished";
  const waiting = !finished && notOpenYet(s);
  const tag = s.kind ? { label: CALL_KINDS[s.kind] ?? s.kind, color: "brand" } : stateOf(s.state);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex flex-col gap-3 rounded-lg border border-slate-100 bg-white p-5 text-left shadow-sm transition hover:border-brand/40 hover:shadow"
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-semibold text-slate-900">{s.name}</h3>
        <Tag color={tag.color}>{tag.label}</Tag>
      </div>
      <p className="text-sm text-slate-500">
        {s.kind ? "Studies abroad" : destinationOf(s)} · {closingOf(s)}
      </p>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div className={finished ? "h-full bg-slate-300" : "h-full bg-brand"} style={{ width: `${s.applicationsToEvaluate ? (100 * s.mySubmitted) / s.applicationsToEvaluate : 0}%` }} />
      </div>
      <p className="text-sm text-slate-700">
        <span className="font-semibold">{s.mySubmitted}</span> of {s.applicationsToEvaluate} marked
        {s.myDrafts > 0 && ` · ${s.myDrafts} in progress`}
        {s.myConflicts > 0 && ` · ${s.myConflicts} removed for a conflict`}
      </p>
      {finished ? (
        <span className="text-sm text-slate-500">Marking is over</span>
      ) : waiting ? (
        <span className="text-sm font-medium text-slate-600">{opensNote(s.closesAt)}</span>
      ) : (
        <span className={`text-sm font-medium ${left > 0 ? "text-amber-700" : "text-green-700"}`}>{left > 0 ? `${left} left to mark` : "All marked · waiting for HEC's decision"}</span>
      )}
    </button>
  );
}

// The scholarships and abroad loan calls the evaluator marks, as one list of cards
const useMarkingCalls = () => {
  const scholarships = useQuery({
    queryKey: [...EVALUATOR_KEY, "scholarships"],
    queryFn: async () => (await scholarshipsApi.get("evaluator/scholarships", { params: pageParams({ size: 100 }) })).data?.content ?? [],
  });
  const abroad = useAbroadMarkingCalls();
  return {
    data: scholarships.data && abroad.data ? [...scholarships.data, ...abroad.data.map(abroadCardOf)] : undefined,
    isPending: scholarships.isPending || abroad.isPending,
    isError: scholarships.isError || abroad.isError,
    refetch: () => Promise.all([scholarships.refetch(), abroad.refetch()]),
  };
};

// The evaluator's calls by group, each sorted as its page lists them
const groupScholarships = (scholarships = []) => {
  const groups = Object.fromEntries(MARKING_GROUPS.map((g) => [g.value, []]));
  scholarships.forEach((s) => groups[groupOf(s)].push(s));
  groups["to-mark"].sort(byClosing);
  groups.marked.sort(byClosing);
  groups.finished.sort((a, b) => byClosing(b, a));
  return groups;
};

// Journey step 9: one group of the calls this evaluator marks
export function MarkingGroupPage({ group }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const current = MARKING_GROUPS.find((g) => g.value === group);
  const { data, isPending, isError, refetch } = useMarkingCalls();
  const scholarships = groupScholarships(data)[group];

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader title={current.title} subtitle={current.subtitle} />
      {isPending ? (
        <Loading label="Loading your calls…" />
      ) : isError ? (
        <EmptyState title="Could not load your calls" onRefresh={refetch} />
      ) : !data.length ? (
        <EmptyState title="You are not on any review committee" description="HEC assigns evaluators when a scholarship or loan call is set up." />
      ) : scholarships.length ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {scholarships.map((s) => (
            <MarkingCard key={s.id} scholarship={s} onOpen={() => navigate(s.path ?? `/evaluation/${s.id}`, { state: { from: pathname } })} />
          ))}
        </div>
      ) : (
        <EmptyState title={current.empty} />
      )}
    </Container>
  );
}

// /evaluation (and the former ?tab= links): the asked group, otherwise the first one with calls
export function MarkingRedirect() {
  const [params] = useSearchParams();
  const asked = MARKING_GROUPS.find((g) => g.value === params.get("tab"))?.value;
  const { data, isPending } = useMarkingCalls();
  if (!asked && isPending) return <Loading label="Loading your calls…" />;
  const groups = groupScholarships(data);
  const group = asked ?? MARKING_GROUPS.find((g) => groups[g.value].length)?.value ?? "to-mark";
  return <Navigate to={`/evaluation/${group}`} replace />;
}

// One scholarship's applications for this evaluator
export function EvaluatorApplicationsPage() {
  const { scholarshipId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Back to the group page the evaluator came from
  const back = useLocation().state?.from ?? "/evaluation";
  const [evaluationState, setEvaluationState] = useState("");
  const [page, setPage] = useState(0);
  const [removing, setRemoving] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const { data, isPending, isError, error } = useQuery({
    queryKey: [...EVALUATOR_KEY, "applications", scholarshipId, evaluationState, page],
    placeholderData: (previous) => previous,
    retry: (count, err) => !["consent-required", MARKING_NOT_OPEN].includes(problemOf(err)?.code) && count < 2,
    queryFn: async () => (await scholarshipsApi.get(`evaluator/scholarships/${scholarshipId}/applications`, { params: pageParams({ evaluationState, page, size: PAGE_SIZE }) })).data,
  });
  const needsConsent = isError && problemOf(error)?.code === "consent-required";
  const notOpen = isError && problemOf(error)?.code === MARKING_NOT_OPEN;

  const putBack = async (r) => {
    setRestoring(r.applicationId);
    await restoreApplicant(queryClient, r.applicationId);
    setRestoring(null);
  };

  const columns = [
    { key: "applicant", label: "Applicant", accessor: (r) => fullName(r.applicant), render: (r) => <ApplicantName name={fullName(r.applicant)} meets={r.meetsEligibility} /> },
    { key: "programme", label: "Programme", accessor: (r) => r.programme.name },
    { key: "submittedAt", label: "Submitted", accessor: (r) => formatDateTime(r.submittedAt), sortValue: (r) => r.submittedAt },
    {
      key: "state",
      label: "My marking",
      accessor: (r) => evaluationStateOf(r.myEvaluationState).label,
      render: (r) => <Tag color={evaluationStateOf(r.myEvaluationState).color}>{evaluationStateOf(r.myEvaluationState).label}</Tag>,
    },
    {
      key: "open",
      label: "",
      exportable: false,
      render: (r) =>
        r.myEvaluationState === "CONFLICTED" ? (
          <RowAction Icon={ArrowUturnLeftIcon} label="Put back" onClick={() => putBack(r)} loading={restoring === r.applicationId} />
        ) : (
          <span className="flex justify-end gap-1">
            {r.myEvaluationState !== "SUBMITTED" && <RowAction Icon={UserMinusIcon} label="Remove (conflict)" variant="danger-soft" onClick={() => setRemoving(r)} />}
            <RowAction Icon={ArrowRightIcon} label={r.myEvaluationState === "SUBMITTED" ? "View" : "Mark"} onClick={() => navigate(`/evaluation/applications/${r.applicationId}`)} />
          </span>
        ),
    },
  ];

  return (
    <Container className="flex flex-col gap-6 py-6">
      <Link to={back} className="flex w-fit items-center gap-1 text-sm font-medium text-brand hover:underline">
        <ArrowLeftIcon className="h-4 w-4" /> My scholarships
      </Link>
      <PageHeader title="Applications to review" />
      {needsConsent ? (
        <ConsentCard scholarshipId={scholarshipId} />
      ) : notOpen ? (
        <EmptyState title="Marking has not started" description={opensNote()} />
      ) : (
      <Table cardView viewKey="evaluator-applications"
        columns={columns}
        data={data?.content ?? []}
        isLoading={isPending}
        searchable={false}
        exportable={false}
        emptyTitle={isError ? "Could not load the applications." : "No applications"}
        pagination={{ page, totalPages: data?.totalPages ?? 0, totalElements: data?.totalElements, pageSize: PAGE_SIZE, onPageChange: setPage }}
        filters={
          <Select
            aria-label="My marking"
            placeholder="All"
            value={evaluationState}
            onChange={(e) => {
              setEvaluationState(e.target.value);
              setPage(0);
            }}
            options={EVALUATION_STATES}
            className="w-full sm:w-56"
          />
        }
      />
      )}
      <RemoveApplicantModal applicant={removing && fullName(removing.applicant)} applicationId={removing?.applicationId} onClose={() => setRemoving(null)} onRemoved={() => setRemoving(null)} />
    </Container>
  );
}
